import React from "react";
import { Box, Text, Flex, SimpleGrid } from "@chakra-ui/react";
import { motion } from "framer-motion";

// =========================
// Custom SVG Icons (Chakra)
// =========================

const DatabaseIcon = (props) => (
  <svg
    width="26"
    height="26"
    viewBox="0 0 24 24"
    stroke="#2AA198"
    strokeWidth="2"
    fill="none"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path d="M3 5v6c0 1.66 4.03 3 9 3s9-1.34 9-3V5" />
    <path d="M3 11v6c0 1.66 4.03 3 9 3s9-1.34 9-3v-6" />
  </svg>
);

const SparklesIcon = (props) => (
  <svg
    width="26"
    height="26"
    viewBox="0 0 24 24"
    stroke="#2AA198"
    strokeWidth="2"
    fill="none"
    {...props}
  >
    <path d="M12 3v4" />
    <path d="M12 17v4" />
    <path d="M3 12h4" />
    <path d="M17 12h4" />
    <path d="M5.6 5.6l2.8 2.8" />
    <path d="M15.6 15.6l2.8 2.8" />
    <path d="M5.6 18.4l2.8-2.8" />
    <path d="M15.6 8.4l2.8-2.8" />
  </svg>
);

const LightbulbIcon = (props) => (
  <svg
    width="26"
    height="26"
    viewBox="0 0 24 24"
    stroke="#2AA198"
    strokeWidth="2"
    fill="none"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <circle cx="12" cy="12" r="5" />
    <path d="M9 18h6" />
    <path d="M10 22h4" />
  </svg>
);

// =========================
// Motion Variants
// =========================

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.25, duration: 0.6, ease: "easeOut" }
  })
};

const numberVariants = {
  hidden: { opacity: 0, x: -20, scale: 0.5 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    scale: 1,
    transition: { delay: i * 0.25 + 0.2, duration: 0.5 }
  })
};

const MotionBox = motion(Box);

// =========================
// COMPONENT
// =========================

const IntelligenceSection = () => {
  const steps = [
    {
      number: "01",
      icon: <DatabaseIcon />,
      title: "We gather public data",
      text: "Market reports, competitor signals, industry trends—all collected from public sources automatically."
    },
    {
      number: "02",
      icon: <SparklesIcon />,
      title: "AI finds the patterns",
      text: "Our intelligence layer analyzes everything to surface what matters most to your business."
    },
    {
      number: "03",
      icon: <LightbulbIcon />,
      title: "You get clear guidance",
      text: "Plain-language insights and recommendations—no dashboards, no analyst required."
    }
  ];

  return (
    <Box w="full" py="28" px="6" bg="#F4F7FA">

      {/* Heading */}
      <Box textAlign="center" maxW="3xl" mx="auto" mb="20">
        <Text
          fontSize="sm"
          letterSpacing="wide"
          color="#345C9C"
          fontWeight="semibold"
          mb="3"
        >
          HOW IT WORKS
        </Text>

        <Text
          fontSize="5xl"
          fontWeight="bold"
          fontFamily="serif"
          color="#1A2C4E"
          mb="4"
        >
          Simple by <Text as="span" color="#2AA198">design</Text>
        </Text>

        <Text fontSize="lg" color="gray.600">
          No setup, no internal data required. Just market intelligence that works.
        </Text>
      </Box>

      {/* STEP CARDS */}
      <Flex
        direction={{ base: "column", md: "row" }}
        justify="center"
        align="flex-start"
        gap="10"
        maxW="7xl"
        mx="auto"
      >
        {steps.map((step, i) => (
          <MotionBox
            key={i}
            custom={i}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={cardVariants}
            bg="white"
            rounded="3xl"
            p="10"
            w={{ base: "100%", md: "33%" }}
            boxShadow="0px 6px 20px rgba(0,0,0,0.06)"
            border="1px solid #E6EBF2"
            position="relative"
          >
            {/* Number Badge */}
            <motion.div
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={numberVariants}
              style={{
                width: "64px",
                height: "64px",
                background: "#274D8A",
                color: "white",
                borderRadius: "50%",
                fontWeight: "bold",
                fontSize: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "absolute",
                top: "-32px",
                left: "32px"
              }}
            >
              {step.number}
            </motion.div>

            {/* Icon */}
            <Flex align="center" gap="3" mt="10" mb="6">
              <Flex
                w="14"
                h="14"
                bg="#E4EFEB"
                rounded="xl"
                align="center"
                justify="center"
              >
                {step.icon}
              </Flex>
            </Flex>

            {/* Title */}
            <Text
              fontSize="2xl"
              fontWeight="bold"
              fontFamily="serif"
              color="#1A2C4E"
              mb="3"
            >
              {step.title}
            </Text>

            {/* Description */}
            <Text fontSize="17px" color="gray.600" lineHeight="1.7">
              {step.text}
            </Text>

            {/* Arrow Connector */}
            {i < steps.length - 1 && (
              <Flex
                display={{ base: "none", md: "flex" }}
                position="absolute"
                top="50%"
                right="-24px"
                transform="translateY(-50%)"
                w="12"
                h="12"
                bg="white"
                rounded="full"
                border="1px solid #E6EBF2"
                align="center"
                justify="center"
                boxShadow="md"
              >
                <Text fontSize="24px" color="#5776A3">➜</Text>
              </Flex>
            )}
          </MotionBox>
        ))}
      </Flex>
    </Box>
  );
};

export default IntelligenceSection;